import { Dispatch, useEffect, useState, MouseEvent, useCallback, SetStateAction } from 'react';
import styled from 'styled-components';

import SegmentedControl from '@ingka/segmented-control';
import { SnapValidationResult, ValidSnapActionCandidateOrErrors } from '@inter-ikea-core/core';

import { useCore } from 'contexts/core';
import { mmToCm } from 'utils/measurementUtils';

import AdjustableSlider, { isWithinRange } from './AdjustableSlider';
import { isMobile } from 'utils/platform';

const Control = styled(SegmentedControl)`
  margin-block-start: 0.75rem;
  padding-inline: ${isMobile() ? '1rem' : ''};
`;

type ControlButtonItems = {
  label: string;
  value: number;
}[];

type SnapResult = ValidSnapActionCandidateOrErrors | SnapValidationResult;

type SegmentedControlProps = {
  items: ControlButtonItems;
  unit: string;
  currentSize: number;
  setCurrentSize: Dispatch<SetStateAction<number | undefined>>;
  onButtonClick: (size: number) => void | SnapResult | undefined;
  onSliderChange: (size: number) => void;
};

const RANGE_CM = { min: 20, max: 30 };

const AdjustableController = ({
  items,
  unit,
  currentSize,
  onButtonClick,
  onSliderChange
}: SegmentedControlProps) => {
  const coreApi = useCore();
  const [showAdjustableSlider, setShowAdjustableSlider] = useState(false);
  const [index, setIndex] = useState<number>();

  const handleOnClick = (e: MouseEvent<HTMLButtonElement>, index: number): void => setIndex(index);

  const getCurrentSizeIndex = useCallback(() => {
    if (currentSize) {
      const i = items.findIndex(
        i => i.value === Math.round(currentSize) || isWithinRange(mmToCm(i.value), RANGE_CM)
      );
      setIndex(i);
      if (isWithinRange(mmToCm(currentSize), RANGE_CM)) {
        setShowAdjustableSlider(true);
      }
    }
  }, [currentSize, items]);

  useEffect(() => {
    getCurrentSizeIndex();
  }, [currentSize, getCurrentSizeIndex]);

  useEffect(() => {
    if (index !== undefined) {
      switch (items[index].value) {
        case 800:
        case 600:
          setShowAdjustableSlider(false);
          const res = onButtonClick(items[index].value);
          res && !res.valid && getCurrentSizeIndex();
          break;
        case 200:
          if (currentSize && !isWithinRange(mmToCm(currentSize), RANGE_CM))
            onButtonClick(items[index].value);
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, items, coreApi.core]);

  return (
    <>
      <Control activeIndex={index} items={items} onClick={handleOnClick} />
      {showAdjustableSlider && (
        <AdjustableSlider initialSize={currentSize} unit={unit} onSliderChange={onSliderChange} />
      )}
    </>
  );
};

export default AdjustableController;
